import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { statsService } from '../../services/api';
import { format } from 'date-fns';
import './Statistics.css';

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await statsService.getAdminStats();
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!stats) return null;

  const StatCard = ({ title, value, subtitle, icon, color = 'primary', trend }) => (
    <Card className="stat-card h-100">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="stat-label text-muted">{title}</div>
            <div className={`stat-value text-${color}`}>{value}</div>
            {subtitle && <div className="stat-subtitle text-muted">{subtitle}</div>}
            {trend && (
              <div className={`stat-trend ${trend > 0 ? 'text-success' : 'text-danger'}`}>
                <i className={`bi bi-arrow-${trend > 0 ? 'up' : 'down'}`}></i> {Math.abs(trend)}%
              </div>
            )}
          </div>
          {icon && (
            <div className={`stat-icon bg-${color} bg-opacity-10 text-${color}`}>
              <i className={`bi bi-${icon}`}></i>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
      waitingList: 'info',
      upcoming: 'primary',
      ongoing: 'success',
      completed: 'secondary',
      cancelled: 'danger'
    };
    return <Badge bg={colors[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <div className="statistics-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Statistics & Analytics</h2>
        <button className="btn btn-outline-primary btn-sm" onClick={fetchStats}>
          <i className="bi bi-arrow-clockwise me-2"></i>Refresh
        </button>
      </div>

      {/* Overview Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <StatCard
            title="Total Users"
            value={stats.overview.totalUsers}
            subtitle={`${stats.overview.adminCount} admins, ${stats.overview.regularUserCount} members`}
            icon="people-fill"
            color="primary"
          />
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <StatCard
            title="Total Trips"
            value={stats.overview.totalTrips}
            subtitle={`${stats.trips.upcoming} upcoming`}
            icon="compass"
            color="success"
          />
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <StatCard
            title="Total Applications"
            value={stats.overview.totalApplications}
            subtitle={`${stats.applications.pending} pending review`}
            icon="file-text"
            color="warning"
          />
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <StatCard
            title="Conversion Rate"
            value={`${stats.overview.conversionRate}%`}
            subtitle={`${stats.applications.approved} approved`}
            icon="graph-up-arrow"
            color="info"
          />
        </Col>
      </Row>

      {/* Performance Metrics */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-speedometer2 me-2"></i>Capacity Utilization
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="capacity-overview">
                <div className="capacity-stats mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Capacity:</span>
                    <strong>{stats.capacityStats.totalCapacity} spots</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Booked Spots:</span>
                    <strong>{stats.capacityStats.totalBooked} spots</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span>Utilization Rate:</span>
                    <strong className="text-primary">{stats.overview.capacityUtilization}%</strong>
                  </div>
                </div>
                <div className="progress" style={{ height: '30px' }}>
                  <div
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{ width: `${stats.overview.capacityUtilization}%` }}
                  >
                    {stats.overview.capacityUtilization}%
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-currency-euro me-2"></i>Revenue Overview
              </h5>
            </Card.Header>
            <Card.Body>
              {stats.revenue && stats.revenue.length > 0 ? (
                stats.revenue.map((rev, idx) => (
                  <div key={idx} className="revenue-item mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Currency:</span>
                      <strong>{rev._id}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Actual Revenue:</span>
                      <strong className="text-success">
                        {rev.totalRevenue.toLocaleString()} {rev._id}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Potential Revenue:</span>
                      <strong className="text-info">
                        {rev.potentialRevenue.toLocaleString()} {rev._id}
                      </strong>
                    </div>
                    {idx < stats.revenue.length - 1 && <hr />}
                  </div>
                ))
              ) : (
                <p className="text-muted">No revenue data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Trip & Application Status */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-diagram-3 me-2"></i>Trip Status Distribution
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="status-list">
                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('upcoming')} Upcoming</span>
                    <strong className="text-primary">{stats.trips.upcoming}</strong>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-primary"
                      style={{ width: `${(stats.trips.upcoming / stats.overview.totalTrips) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('ongoing')} Ongoing</span>
                    <strong className="text-success">{stats.trips.ongoing}</strong>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-success"
                      style={{ width: `${(stats.trips.ongoing / stats.overview.totalTrips) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('completed')} Completed</span>
                    <strong className="text-secondary">{stats.trips.completed}</strong>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-secondary"
                      style={{ width: `${(stats.trips.completed / stats.overview.totalTrips) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('cancelled')} Cancelled</span>
                    <strong className="text-danger">{stats.trips.cancelled}</strong>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-danger"
                      style={{ width: `${(stats.trips.cancelled / stats.overview.totalTrips) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-clipboard-check me-2"></i>Application Status
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="status-list">
                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('pending')} Pending</span>
                    <strong className="text-warning">{stats.applications.pending}</strong>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-warning"
                      style={{ width: `${(stats.applications.pending / stats.overview.totalApplications) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('approved')} Approved</span>
                    <strong className="text-success">{stats.applications.approved}</strong>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-success"
                      style={{ width: `${(stats.applications.approved / stats.overview.totalApplications) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('waitingList')} Waiting List</span>
                    <strong className="text-info">{stats.applications.waitingList}</strong>
                  </div>
                  <div className="progress mb-3" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-info"
                      style={{ width: `${(stats.applications.waitingList / stats.overview.totalApplications) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="status-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{getStatusBadge('rejected')} Rejected</span>
                    <strong className="text-danger">{stats.applications.rejected}</strong>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div
                      className="progress-bar bg-danger"
                      style={{ width: `${(stats.applications.rejected / stats.overview.totalApplications) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Popular Destinations */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-star-fill me-2"></i>Popular Destinations
              </h5>
            </Card.Header>
            <Card.Body>
              {stats.popularDestinations && stats.popularDestinations.length > 0 ? (
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th className="text-center">Trips</th>
                      <th className="text-center">Participants</th>
                      <th className="text-center">Fill Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.popularDestinations.map((dest, idx) => (
                      <tr key={idx}>
                        <td>
                          <i className="bi bi-geo-alt-fill text-primary me-2"></i>
                          {dest._id}
                        </td>
                        <td className="text-center">
                          <Badge bg="primary" pill>{dest.tripCount}</Badge>
                        </td>
                        <td className="text-center">{dest.totalApproved}/{dest.totalCapacity}</td>
                        <td className="text-center">
                          <span className={`badge ${
                            (dest.totalApproved / dest.totalCapacity) * 100 >= 80
                              ? 'bg-success'
                              : (dest.totalApproved / dest.totalCapacity) * 100 >= 50
                              ? 'bg-warning'
                              : 'bg-danger'
                          }`}>
                            {dest.totalCapacity > 0
                              ? Math.round((dest.totalApproved / dest.totalCapacity) * 100)
                              : 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No destination data available</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Upcoming Trips */}
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-calendar-event me-2"></i>Upcoming Trips (Next 30 Days)
              </h5>
            </Card.Header>
            <Card.Body>
              {stats.upcomingTrips && stats.upcomingTrips.length > 0 ? (
                <div className="upcoming-trips-list">
                  {stats.upcomingTrips.map((trip) => (
                    <div key={trip._id} className="trip-item mb-3 pb-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">{trip.name}</div>
                          <div className="small text-muted">
                            <i className="bi bi-geo-alt me-1"></i>
                            {trip.location}
                          </div>
                          <div className="small text-muted">
                            <i className="bi bi-calendar3 me-1"></i>
                            {format(new Date(trip.startDate), 'MMM d, yyyy')}
                          </div>
                        </div>
                        <div className="text-end">
                          <Badge bg="primary" className="mb-1">
                            {trip.approvedCount || 0}/{trip.capacity}
                          </Badge>
                          <div className="small text-muted">spots filled</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No upcoming trips in the next 30 days</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Applications */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>Recent Applications
              </h5>
            </Card.Header>
            <Card.Body>
              {stats.recentApplications && stats.recentApplications.length > 0 ? (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Trip</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentApplications.map((app) => (
                      <tr key={app._id}>
                        <td>
                          <i className="bi bi-person-circle me-2"></i>
                          {app.userId?.name || 'Unknown'}
                        </td>
                        <td>{app.tripId?.name || 'N/A'}</td>
                        <td>
                          <i className="bi bi-geo-alt me-1"></i>
                          {app.tripId?.location || 'N/A'}
                        </td>
                        <td>{getStatusBadge(app.status)}</td>
                        <td className="text-muted small">
                          {format(new Date(app.createdAt), 'MMM d, yyyy HH:mm')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No recent applications</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Statistics;
